import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';
import {
  User,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Eye,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useOperators,
  useCreateOperator,
  useUpdateOperator,
  useDeleteOperator,
  useSetOperatorHours,
} from '@/hooks/useOperators';
import type { Operator } from '@/api/operators.api';

export const Route = createFileRoute('/_authenticated/usuarios')({
  component: UsuariosPage,
});

const ROLE_LABELS: Record<string, string> = {
  master: 'Master',
  admin: 'Admin',
  operator: 'Operador',
  viewer: 'Viewer',
};

const ROLE_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  master: 'destructive',
  admin: 'default',
  operator: 'secondary',
  viewer: 'outline',
};

const ROLE_ICONS: Record<string, React.ReactNode> = {
  master: <ShieldAlert className="h-4 w-4" />,
  admin: <ShieldCheck className="h-4 w-4" />,
  operator: <Shield className="h-4 w-4" />,
  viewer: <Eye className="h-4 w-4" />,
};

// Secciones válidas = rutas del sidebar (Sidebar.tsx SECTIONS). Vacío = ve todo.
const SECTIONS_OPTIONS: { to: string; label: string }[] = [
  { to: '/chats', label: 'Chats' },
  { to: '/usuarios', label: 'Usuarios' },
  { to: '/clientes', label: 'Clientes' },
  { to: '/retiros', label: 'Retiros' },
  { to: '/cuentas', label: 'Cuentas' },
  { to: '/auditoria', label: 'Auditoría' },
  { to: '/mensajes', label: 'Mensajes' },
  { to: '/notificaciones', label: 'Notificaciones' },
  { to: '/eventos', label: 'Eventos' },
  { to: '/bonos', label: 'Bonos' },
  { to: '/cupones', label: 'Cupones' },
  { to: '/metricas', label: 'Métricas' },
  { to: '/apis', label: 'APIs' },
  { to: '/ajustes', label: 'Ajustes' },
];

const HHMM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

const formSchema = z
  .object({
    username: z.string().min(2, 'Mínimo 2 caracteres').max(60),
    password: z.string().min(4, 'Mínimo 4 caracteres').max(100),
    displayName: z.string().min(1, 'Requerido').max(120),
    role: z.enum(['master', 'admin', 'operator', 'viewer']),
    permissions: z.array(z.string()).default([]),
    hoursStart: z
      .string()
      .regex(HHMM_REGEX, 'Formato HH:MM')
      .optional()
      .or(z.literal('')),
    hoursEnd: z
      .string()
      .regex(HHMM_REGEX, 'Formato HH:MM')
      .optional()
      .or(z.literal('')),
  })
  .refine((v) => Boolean(v.hoursStart) === Boolean(v.hoursEnd), {
    message: 'Completá inicio y fin del horario, o dejá ambos vacíos',
    path: ['hoursEnd'],
  });

type FormValues = z.infer<typeof formSchema>;

function UsuariosPage() {
  const { data: operators = [], isLoading } = useOperators();
  const createMutation = useCreateOperator();
  const updateMutation = useUpdateOperator();
  const deleteMutation = useDeleteOperator();
  const setHoursMutation = useSetOperatorHours();

  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Operator | null>(null);
  const [deleting, setDeleting] = useState<Operator | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
      displayName: '',
      role: 'operator',
      permissions: [],
      hoursStart: '',
      hoursEnd: '',
    },
  });

  const onSubmit = async (values: FormValues) => {
    let operatorId = editing?.id;
    if (editing) {
      await updateMutation.mutateAsync({
        id: editing.id,
        body: {
          displayName: values.displayName,
          role: values.role,
          permissions: values.permissions,
        },
      });
    } else {
      const created = await createMutation.mutateAsync({
        username: values.username,
        password: values.password,
        displayName: values.displayName,
        role: values.role,
        permissions: values.permissions,
      });
      operatorId = created.id;
    }

    // El horario vive aparte (ApiConfig operator_hours). Solo se toca si
    // el operador ya existe y se cargaron ambos campos.
    if (operatorId && values.hoursStart && values.hoursEnd) {
      await setHoursMutation.mutateAsync({
        id: operatorId,
        body: { start: values.hoursStart, end: values.hoursEnd },
      });
    }

    setIsOpen(false);
    setEditing(null);
    form.reset();
  };

  const handleEdit = (op: Operator) => {
    setEditing(op);
    form.reset({
      username: op.username,
      password: '',
      displayName: op.displayName,
      role: op.role,
      permissions: op.permissions ?? [],
      hoursStart: '',
      hoursEnd: '',
    });
    setIsOpen(true);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    await deleteMutation.mutateAsync(deleting.id);
    setDeleting(null);
  };

  const columns = useMemo<ColumnDef<Operator>[]>(
    () => [
      {
        accessorKey: 'displayName',
        header: 'Nombre',
        cell: ({ row }) => (
          <div className="font-medium">{row.original.displayName}</div>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Usuario',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.original.username}</span>
        ),
      },
      {
        accessorKey: 'role',
        header: 'Rol',
        cell: ({ row }) => (
          <Badge variant={ROLE_VARIANTS[row.original.role] || 'outline'} className="gap-1">
            {ROLE_ICONS[row.original.role]}
            {ROLE_LABELS[row.original.role] || row.original.role}
          </Badge>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Estado',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'default' : 'secondary'}>
            {row.original.isActive ? 'Activo' : 'Inactivo'}
          </Badge>
        ),
      },
      {
        accessorKey: 'lastLoginAt',
        header: 'Último login',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.lastLoginAt
              ? new Date(row.original.lastLoginAt).toLocaleString('es-AR')
              : 'Nunca'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button size="icon" variant="ghost" onClick={() => handleEdit(row.original)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-red-500"
              onClick={() => setDeleting(row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <>
      <TopBanner
        title="Usuarios"
        subtitle="Operadores del panel"
        action={
          <Button
            onClick={() => {
              setEditing(null);
              form.reset({
                username: '',
                password: '',
                displayName: '',
                role: 'operator',
                permissions: [],
                hoursStart: '',
                hoursEnd: '',
              });
              setIsOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuevo operador
          </Button>
        }
      />
      <div className="flex-1 overflow-auto p-6">
        <DataTable
          columns={columns}
          data={operators}
          isLoading={isLoading}
          searchPlaceholder="Buscar operador..."
          searchColumn="displayName"
        />
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar operador' : 'Nuevo operador'}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Usuario</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={!!editing} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {!editing && (
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contraseña</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre visible</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rol</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="master">Master</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="operator">Operador</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="permissions"
                render={({ field }) => {
                  const selected = field.value ?? [];
                  const toggle = (to: string) => {
                    field.onChange(
                      selected.includes(to)
                        ? selected.filter((s) => s !== to)
                        : [...selected, to],
                    );
                  };
                  return (
                    <FormItem>
                      <FormLabel>Secciones visibles</FormLabel>
                      <p className="text-xs text-muted-foreground">
                        Sin selección = ve todas las secciones del panel.
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-md border p-3">
                        {SECTIONS_OPTIONS.map((s) => (
                          <label
                            key={s.to}
                            className="flex items-center gap-2 text-sm font-normal"
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-input"
                              checked={selected.includes(s.to)}
                              onChange={() => toggle(s.to)}
                            />
                            {s.label}
                          </label>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="hoursStart"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Horario desde (opcional)</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="hoursEnd"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Horario hasta (opcional)</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending || setHoursMutation.isPending
                  }
                >
                  {(createMutation.isPending || updateMutation.isPending || setHoursMutation.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar operador?</DialogTitle>
            <DialogDescription>
              {deleting && (
                <>
                  Se eliminará <strong>{deleting.displayName}</strong> ({deleting.username}).
                  Esta acción no se puede deshacer.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
